import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface RealtorAd {
  id: string;
  image_url: string;
  is_active: boolean;
  ad_type: 'buyer' | 'seller';
}

interface SelectedAd {
  id: string;
  image_url: string;
  ad_type: 'buyer' | 'seller';
}

// Randomly select one ad from an array
function selectRandomAd(ads: RealtorAd[]): SelectedAd | null {
  if (ads.length === 0) return null;
  const randomIndex = Math.floor(Math.random() * ads.length);
  const selected = ads[randomIndex];
  return { id: selected.id, image_url: selected.image_url, ad_type: selected.ad_type };
}

export function useRealtorAds(realtorProfileId: string | null, adTypeFilter?: 'buyer' | 'seller' | null) {
  return useQuery({
    queryKey: ['realtor-ads', realtorProfileId, adTypeFilter],
    queryFn: async (): Promise<SelectedAd | null> => {
      if (!realtorProfileId) return null;
      
      let query = supabase
        .from('realtor_ads')
        .select('id, image_url, ad_type')
        .eq('realtor_profile_id', realtorProfileId)
        .eq('is_active', true);

      // If a specific ad type is requested, filter by it
      if (adTypeFilter) {
        query = query.eq('ad_type', adTypeFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching realtor ads:', error);
        return null;
      }

      const ads = (data || []) as RealtorAd[];
      
      // If filtering by type returned no results, fall back to all active ads
      if (ads.length === 0 && adTypeFilter) {
        const { data: allAds } = await supabase
          .from('realtor_ads')
          .select('id, image_url, ad_type')
          .eq('realtor_profile_id', realtorProfileId)
          .eq('is_active', true);
        
        return selectRandomAd((allAds || []) as RealtorAd[]);
      }
      
      // Randomly select one from available ads
      return selectRandomAd(ads);
    },
    staleTime: 0, // Always fresh to enable rotation on each page view
    gcTime: 0, // Don't cache - each page load should get a new random ad
    enabled: !!realtorProfileId,
  });
}

// Function to track ad clicks via secure edge function with rate limiting
export async function trackRealtorAdClick(adId: string) {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-ad-click`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ ad_id: adId, ad_type: 'realtor' }),
      }
    );
    if (!response.ok) {
      console.error('Error tracking ad click:', response.statusText);
    }
  } catch (err) {
    console.error('Error tracking ad click:', err);
  }
}
